import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, FileText, Search, Copy } from 'lucide-react';
import { useTemplates, useCreateTemplate } from '../../../hooks/useTemplates';
import { getCategoryStyle } from '../../../utils/colors';
import { formatRelativeTime } from '../../../utils/formatters';
import { useToast } from '../../../contexts/ToastContext';

const CATEGORIES = ['All', 'confirmation', 'reminder', 'payment', 'promo', 'other'];

function EmailTemplateList() {
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [activeFilter, setActiveFilter] = useState('All');
  const createMutation = useCreateTemplate();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const filters = {};
  if (debouncedSearch) filters.search = debouncedSearch;
  if (category !== 'All') filters.category = category;
  if (activeFilter !== 'All') filters.isActive = activeFilter === 'Active' ? 'true' : 'false';

  const { templates, total, isLoading, error } = useTemplates(filters);

  const handleRowClick = useCallback((id) => {
    navigate(`/admin/email/templates/${id}`);
  }, [navigate]);

  const handleDuplicate = useCallback(async (e, tpl) => {
    e.stopPropagation();
    try {
      const result = await createMutation.mutateAsync({
        name: `${tpl.name} (Copy)`,
        category: tpl.category,
        subject_tpl: tpl.subject_tpl,
        body_tpl: tpl.body_tpl,
        required_fields: tpl.required_fields || [],
        allowed_merge_fields: tpl.allowed_merge_fields || [],
      });
      toast.success('Template duplicated');
      navigate(`/admin/email/templates/${result.data.id}`);
    } catch (err) {
      toast.error('Failed to duplicate template');
    }
  }, [createMutation, navigate, toast]);

  return (
    <div className="email-page">
      <div className="email-page-header">
        <h3>Email Templates</h3>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/admin/email/templates/new')}
        >
          <Plus size={16} />
          New Template
        </button>
      </div>

      <div className="email-filter-bar">
        <div className="email-search-wrapper">
          <Search size={16} className="email-search-icon" />
          <input
            type="text"
            className="email-search-input"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className="email-filter-select"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
        >
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat === 'All' ? 'All Categories' : cat.charAt(0).toUpperCase() + cat.slice(1)}
            </option>
          ))}
        </select>
        <select
          className="email-filter-select"
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
        >
          <option value="All">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      {error && (
        <div className="email-error">Failed to load templates. Please try again.</div>
      )}

      {isLoading ? (
        <div className="email-table-skeleton">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton-row" />
          ))}
        </div>
      ) : templates.length === 0 ? (
        <div className="email-empty-state">
          <FileText size={48} strokeWidth={1} />
          <p>No templates found.</p>
          <p className="text-muted">
            {debouncedSearch || category !== 'All' || activeFilter !== 'All'
              ? 'Try adjusting your filters.'
              : 'Create your first email template to get started.'}
          </p>
        </div>
      ) : (
        <>
          <div className="email-table-wrapper">
            <table className="email-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Subject</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Last Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {templates.map((tpl) => (
                  <tr
                    key={tpl.id}
                    className="email-table-row"
                    onClick={() => handleRowClick(tpl.id)}
                  >
                    <td className="email-table-name">{tpl.name}</td>
                    <td>
                      <span
                        className="email-category-badge"
                        style={getCategoryStyle(tpl.category)}
                      >
                        {tpl.category}
                      </span>
                    </td>
                    <td className="email-table-subject">
                      {tpl.subject_tpl.length > 50
                        ? tpl.subject_tpl.substring(0, 50) + '...'
                        : tpl.subject_tpl}
                    </td>
                    <td>v{tpl.version}</td>
                    <td>
                      <span className={`email-status-dot ${tpl.is_active ? 'active' : 'inactive'}`} />
                      {tpl.is_active ? 'Active' : 'Inactive'}
                    </td>
                    <td className="email-table-date">
                      {formatRelativeTime(tpl.updated_at)}
                    </td>
                    <td>
                      <button
                        className="cb-remove-btn"
                        title="Duplicate"
                        onClick={(e) => handleDuplicate(e, tpl)}
                      >
                        <Copy size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="email-table-footer">
            {total} template{total !== 1 ? 's' : ''}
          </div>
        </>
      )}
    </div>
  );
}

export default EmailTemplateList;
